import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import MobileHeader from "../../components/MobileHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { useI18n } from "../../lib/i18n";
import {
    ShieldCheck, Shield, Target, PiggyBank, Users as UsersIcon,
    TrendingUp, Activity, CheckCircle2, ChevronRight, Sparkles, Crown,
    Lock,
} from "lucide-react";

const ICONS = {
    "shield-check": ShieldCheck,
    "shield": Shield,
    "target": Target,
    "piggy-bank": PiggyBank,
    "users": UsersIcon,
    "trending-up": TrendingUp,
    "activity": Activity,
    "check-circle": CheckCircle2,
};

const TIER_ICONS = { platinum: Crown, gold: Crown, silver: ShieldCheck, bronze: ShieldCheck, starter: Sparkles };

export default function ScoreBoosters() {
    const nav = useNavigate();
    const { t } = useI18n();
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/credit/boosters").then((r) => setData(r.data));
    }, []);

    if (!data) return <div className="p-12 text-center text-slate-400">Loading…</div>;

    const TierIcon = TIER_ICONS[data.tier.key] || Sparkles;
    const progressPct = (data.unlocked_count / data.total_count) * 100;

    return (
        <div data-testid="boosters-page">
            <MobileHeader title={t("boosters.title")} backTo="/consumer/profile" variant="purple">
                <div className="mt-4 bg-white/15 backdrop-blur rounded-2xl p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-white/80">{t("boosters.tier_now")}</div>
                            <div className="font-display text-2xl font-bold flex items-center gap-2 mt-0.5">
                                <TierIcon size={20} style={{ color: data.tier.color }} fill="currentColor" />
                                {data.tier.label}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-wider text-white/80">{t("boosters.score_now")}</div>
                            <div className="font-display text-2xl font-bold mt-0.5">{data.current_score}</div>
                        </div>
                    </div>
                    <div className="mt-3">
                        <div className="flex justify-between text-[10px] text-white/80 mb-1.5">
                            <span>{t("boosters.unlocked_count", { n: data.unlocked_count, total: data.total_count })}</span>
                            {data.tier.next && <span>{t("boosters.next_tier", { tier: data.tier.next })}</span>}
                        </div>
                        <div className="h-2 rounded-full bg-white/20 overflow-hidden">
                            <div className="h-full rounded-full bg-white transition-all" style={{ width: `${progressPct}%` }} />
                        </div>
                    </div>
                </div>
            </MobileHeader>

            <div className="px-5 -mt-4 pb-4 space-y-4">
                {/* Uplift card */}
                <div className="bg-white rounded-2xl p-4 border border-slate-200/60 product-shadow" data-testid="uplift-card">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{t("boosters.already_earned")}</div>
                            <div className="font-display text-2xl font-bold text-emerald-600 mt-0.5">+{data.earned_uplift}</div>
                            <div className="text-[10px] text-slate-500">{t("boosters.credit_points")}</div>
                        </div>
                        <div className="border-l border-slate-100 pl-3">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{t("boosters.up_for_grabs")}</div>
                            <div className="font-display text-2xl font-bold text-[#FF6B00] mt-0.5">+{data.max_possible_uplift}</div>
                            <div className="text-[10px] text-slate-500">{t("boosters.unlock_to_claim")}</div>
                        </div>
                    </div>
                    {data.max_possible_uplift > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600">
                            {t("boosters.projected", { score: Math.min(850, data.current_score + data.max_possible_uplift) })}
                        </div>
                    )}
                </div>

                {/* Tier perks card */}
                <PerksCard tier={data.tier} perks={data.perks} t={t} />

                {/* Booster list */}
                <div className="space-y-3">
                    {data.boosters.map((b) => <BoosterCard key={b.key} b={b} nav={nav} t={t} />)}
                </div>
            </div>
        </div>
    );
}

function BoosterCard({ b, nav, t }) {
    const Icon = ICONS[b.icon] || Sparkles;
    const weightBg = b.weight === "high" ? "bg-rose-50 text-rose-700" : b.weight === "medium" ? "bg-amber-50 text-amber-700" : "bg-slate-50 text-slate-700";
    return (
        <div
            className={`bg-white rounded-2xl p-4 border product-shadow ${b.unlocked ? "border-emerald-200" : "border-slate-200/60"}`}
            data-testid={`booster-${b.key}`}
        >
            <div className="flex items-start gap-3">
                <div
                    className={`size-12 rounded-2xl flex items-center justify-center text-white flex-shrink-0 relative`}
                    style={{ background: b.unlocked ? "#00D084" : b.color }}
                >
                    {b.unlocked ? <CheckCircle2 size={22} /> : <Icon size={22} />}
                    {!b.unlocked && (
                        <div className="absolute -top-1 -right-1 size-5 rounded-full bg-slate-400 text-white flex items-center justify-center">
                            <Lock size={10} />
                        </div>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <div className="font-display font-semibold text-[#0A1930] truncate">{b.title}</div>
                        <Badge className={`${weightBg} border text-[10px] font-bold flex-shrink-0`}>+{b.points}</Badge>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{b.description}</p>
                    {!b.unlocked && (
                        <Button
                            onClick={() => nav(b.action_url)}
                            size="sm"
                            className="mt-3 h-8 rounded-lg text-xs font-semibold"
                            style={{ background: b.color }}
                            data-testid={`booster-cta-${b.key}`}
                        >
                            {b.cta} <ChevronRight size={12} />
                        </Button>
                    )}
                    {b.unlocked && (
                        <div className="mt-2 text-[10px] uppercase tracking-wider font-bold text-emerald-700 flex items-center gap-1">
                            <CheckCircle2 size={11} /> {t("boosters.status_unlocked")}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function PerksCard({ tier, perks, t }) {
    const hasPerks = perks && (perks.loan_interest_discount > 0 || perks.mmf_yield_boost > 0 || perks.deposit_apr_boost > 0);
    return (
        <div className="bg-white rounded-2xl p-4 border border-slate-200/60 product-shadow" data-testid="perks-card">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="font-display font-semibold text-[#0A1930]">{t("perks.title")}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                        {t("perks.subtitle", { tier: tier?.label || "—" })}
                    </div>
                </div>
                <div
                    className="size-9 rounded-2xl flex items-center justify-center text-white flex-shrink-0"
                    style={{ background: tier?.color || "#64748B" }}
                >
                    <Sparkles size={16} />
                </div>
            </div>
            {hasPerks ? (
                <div className="grid grid-cols-3 gap-2">
                    <PerkTile
                        active={perks.loan_interest_discount > 0}
                        label={t("perks.loan_discount")}
                        value={t("perks.discount_label", { n: stripZeros(perks.loan_interest_discount * 100) })}
                        accent="#FF0055"
                        testid="perk-loan"
                    />
                    <PerkTile
                        active={perks.mmf_yield_boost > 0}
                        label={t("perks.mmf_boost")}
                        value={t("perks.boost_label", { n: stripZeros(perks.mmf_yield_boost * 100) })}
                        accent="#F59E0B"
                        testid="perk-mmf"
                    />
                    <PerkTile
                        active={perks.deposit_apr_boost > 0}
                        label={t("perks.deposit_boost")}
                        value={t("perks.boost_label", { n: stripZeros(perks.deposit_apr_boost * 100) })}
                        accent="#00D084"
                        testid="perk-deposit"
                    />
                </div>
            ) : (
                <p className="text-xs text-slate-500 leading-relaxed">{t("perks.none")}</p>
            )}
        </div>
    );
}

function PerkTile({ active, label, value, accent, testid }) {
    if (!active) {
        return (
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-2.5 opacity-60" data-testid={testid}>
                <div className="text-[9px] uppercase tracking-wider font-bold text-slate-400 leading-tight">{label}</div>
                <div className="font-display text-base font-bold text-slate-400 mt-1">—</div>
            </div>
        );
    }
    return (
        <div
            className="rounded-xl p-2.5 border"
            style={{ background: `${accent}15`, borderColor: `${accent}40` }}
            data-testid={testid}
        >
            <div className="text-[9px] uppercase tracking-wider font-bold leading-tight" style={{ color: accent }}>
                {label}
            </div>
            <div className="font-display text-base font-bold mt-1" style={{ color: accent }}>
                {value}
            </div>
        </div>
    );
}

function stripZeros(n) {
    // Trim trailing zeros: 0.50 -> "0.5", 10.00 -> "10"
    return Number(n).toFixed(2).replace(/\.?0+$/, "");
}
